import { updateState } from '@angular-architects/ngrx-toolkit';
import { ConversationalConnectorService } from '@klant/conversational-connector/conversational-connector.service';
import { ChatSession } from '@klant/conversational-connector/klant-conversational-connector.model';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { inject } from '@angular/core';
import { pipe, switchMap } from 'rxjs';

import { withDebugDevTools } from '../../../debug/devtools.features';

type ChatState = {
    chatSession: ChatSession | null;
    errorGettingChatSessionIdChat: boolean;
    errorResettingUnreadMessageCounter: boolean;
};

const initialState: ChatState = {
    chatSession: null,
    errorGettingChatSessionIdChat: false,
    errorResettingUnreadMessageCounter: false
};

export type ChatSessionStore = InstanceType<typeof ChatSessionStore>;

export const ChatSessionStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withDebugDevTools('chat-session-store'),
    withMethods((store, conversationalConnectorService = inject(ConversationalConnectorService)) => ({
        getChatSessionId: rxMethod<void>(
            pipe(
                switchMap(() => conversationalConnectorService.generateToken()),
                tapResponse({
                    next: (chatSession: ChatSession) =>
                        updateState(store, 'Chat session was started successfully', { errorGettingChatSessionIdChat: false, chatSession }),
                    error: () => updateState(store, 'Chat session failed to start', { errorGettingChatSessionIdChat: true })
                })
            )
        ),

        resetUnreadMessageCounter: rxMethod<void>(
            pipe(
                switchMap(() => conversationalConnectorService.deleteChatNotifications()),
                tapResponse({
                    next: () => updateState(store, 'Unread message counter was reset', { errorResettingUnreadMessageCounter: false }),
                    error: () => updateState(store, 'Unread message counter failed to reset', { errorResettingUnreadMessageCounter: true })
                })
            )
        ),

        patchChatSession(chatSession: ChatSession | null): void {
            updateState(store, 'Chat session was updated', { chatSession });
        },

        resetStore(): void {
            updateState(store, 'ChatSessionStore was reset', initialState);
        }
    }))
);
